// Display modes: shaded, shaded + edges, wireframe, x-ray and hidden line.
// Edges are LineSegments overlays per body. X-ray and hidden line swap each
// body's material for a mode material and keep the original in
// userData.modeOriginal (ghosting in model-tree.ts works on that original).
import * as THREE from 'three';
import { refreshGhostMaterials } from './model-tree';
import { loadDisplayMode, saveDisplayMode, type DisplayMode } from './preferences';
import { backgroundColor, getModelGroup, isDark, onBackgroundChange, onThemeChange, requestRender } from './viewer';

export type { DisplayMode };

const EDGE_ANGLE = 20;
const EDGE_USERDATA = 'isShadedEdge';
const XRAY_OPACITY = 0.22;

let mode: DisplayMode = 'shaded';
const edgeLines: THREE.LineSegments[] = [];
/** Bodies whose material is currently swapped for x-ray / hidden line. */
const swapped: THREE.Mesh[] = [];
const modeListeners: Array<(mode: DisplayMode) => void> = [];

const hiddenLineMat = new THREE.MeshBasicMaterial({
  toneMapped: false,
  color: backgroundColor(),
  side: THREE.DoubleSide,
  // Push faces back a little so the edge lines on them win the depth test.
  polygonOffset: true,
  polygonOffsetFactor: 1,
  polygonOffsetUnits: 1
});

function edgesWanted(): boolean {
  return mode === 'edges' || mode === 'xray' || mode === 'hidden';
}

function edgeColor(): number {
  if (mode === 'hidden') return isDark() ? 0xe8e6e1 : 0x1b1e24;
  return isDark() ? 0x0a0c10 : 0x1b1e24;
}

function edgeOpacity(): number {
  if (mode === 'hidden') return 1;
  if (mode === 'xray') return isDark() ? 0.7 : 0.6;
  return isDark() ? 0.85 : 0.72;
}

function clearEdgeOverlays(): void {
  for (const line of edgeLines) {
    line.parent?.remove(line);
    line.geometry.dispose();
    (line.material as THREE.Material).dispose();
  }
  edgeLines.length = 0;
}

function rebuildEdgeOverlays(): void {
  clearEdgeOverlays();
  if (!edgesWanted()) {
    requestRender();
    return;
  }
  const model = getModelGroup();
  if (!model) return;

  const mat = new THREE.LineBasicMaterial({
    toneMapped: false,
    color: edgeColor(),
    transparent: true,
    opacity: edgeOpacity(),
    depthTest: true,
    depthWrite: false
  });

  model.traverse(obj => {
    const mesh = obj as THREE.Mesh;
    if (!mesh.isMesh || !mesh.userData.isModelBody || !mesh.geometry) return;
    // One shared material instance per rebuild — clone so dispose is safe per line.
    const lineMat = mat.clone();
    const geo = new THREE.EdgesGeometry(mesh.geometry, EDGE_ANGLE);
    const lines = new THREE.LineSegments(geo, lineMat);
    lines.userData[EDGE_USERDATA] = true;
    lines.renderOrder = 2;
    lines.raycast = () => {};
    mesh.add(lines);
    edgeLines.push(lines);
  });
  mat.dispose();
  refreshGhostMaterials();
  requestRender();
}

function forEachBody(fn: (mesh: THREE.Mesh) => void): void {
  getModelGroup()?.traverse(obj => {
    const mesh = obj as THREE.Mesh;
    if (mesh.isMesh && mesh.userData.isModelBody) fn(mesh);
  });
}

function materialList(m: THREE.Material | THREE.Material[]): THREE.Material[] {
  return Array.isArray(m) ? m : [m];
}

/** Put every swapped body back on its own material. */
function restoreSwapped(): void {
  for (const mesh of swapped) {
    const original = mesh.userData.modeOriginal as THREE.Material | THREE.Material[] | undefined;
    if (!original) continue;
    if (mesh.material !== hiddenLineMat) materialList(mesh.material).forEach(m => m.dispose());
    mesh.material = original;
    delete mesh.userData.modeOriginal;
  }
  swapped.length = 0;
}

function swapMaterials(): void {
  forEachBody(mesh => {
    const original = mesh.material;
    mesh.userData.modeOriginal = original;
    if (mode === 'hidden') {
      mesh.material = hiddenLineMat;
    } else {
      // X-ray: the body's own colours, translucent, without depth writes.
      const xray = materialList(original).map(m => {
        const c = m.clone();
        c.transparent = true;
        c.opacity = XRAY_OPACITY;
        c.depthWrite = false;
        return c;
      });
      mesh.material = Array.isArray(original) ? xray : xray[0];
    }
    swapped.push(mesh);
  });
}

function applyWireframe(on: boolean): void {
  forEachBody(mesh => {
    const lists = [mesh.material, mesh.userData.baseMaterials as THREE.Material[] | undefined];
    for (const list of lists) {
      if (!list) continue;
      for (const m of materialList(list) as THREE.MeshStandardMaterial[]) {
        if ('wireframe' in m) m.wireframe = on;
      }
    }
  });
}

function applyMode(): void {
  // The material is built when this module is imported, before initScene() has
  // applied the stored theme, so its colour would stay on the light background
  // until the theme was next toggled — white faces with white edges in the dark.
  hiddenLineMat.color.set(backgroundColor());
  restoreSwapped();
  applyWireframe(mode === 'wireframe');
  if (mode === 'xray' || mode === 'hidden') swapMaterials();
  rebuildEdgeOverlays();
  requestRender();
}

export function getDisplayMode(): DisplayMode {
  return mode;
}

export function setDisplayMode(next: DisplayMode): void {
  if (next === mode) return;
  mode = next;
  saveDisplayMode(next);
  applyMode();
  for (const cb of modeListeners) cb(mode);
}

export function onDisplayModeChange(cb: (mode: DisplayMode) => void): void {
  modeListeners.push(cb);
}

/** Call after a new model is loaded: the old bodies (and their swaps) are gone. */
export function refreshShadedEdges(): void {
  swapped.length = 0;
  applyMode();
}

export function initDisplayMode(): void {
  mode = loadDisplayMode();
  onThemeChange(() => {
    hiddenLineMat.color.set(backgroundColor());
    if (edgesWanted()) rebuildEdgeOverlays();
  });
  // Hidden line hides faces by painting them in the background colour, so a
  // background set apart from the theme has to reach it too.
  onBackgroundChange(() => {
    hiddenLineMat.color.set(backgroundColor());
  });
}
